import { QuestionStatus } from '@exam-management/graphql';
import {
  Flex,
  Text,
  FormControl,
  FormHelperText,
  FormLabel,
  FormNumberInput,
  FormRadio,
  FormRadioSelection,
  RadioGroup,
  ReadonlyField,
  IconButton,
  BarChartIcon,
  Radio,
} from '@shared/components';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { DEFAULT_CRITERIA, PValueField, DifficultyText } from '../../components';
import { getDifficultyCriteria } from '../../utils';
import type { InformationSectionType } from '../information-section';
import { QUESTION_TYPE_OPTIONS } from '../information-section/constants';

export interface ItemSetSubQuestionInformationFormSectionProps {
  onClick?: () => void;
  status: QuestionStatus;
  mode?: 'workflow' | 'qcontent';
}

export default function ItemSetSubQuestionInformationFormSection(
  props: Readonly<ItemSetSubQuestionInformationFormSectionProps>,
) {
  const { onClick, status, mode = 'qcontent' } = props;
  const formContext = useFormContext<InformationSectionType>();
  const rawPStat = formContext.watch('information.pValue.pStat');

  const pStat = typeof rawPStat === 'number' ? rawPStat.toFixed(2) : '-';
  const currentCriteria = useMemo(() => {
    const normalizedPStat = typeof rawPStat === 'number' ? rawPStat : null;
    return getDifficultyCriteria(DEFAULT_CRITERIA, normalizedPStat);
  }, [rawPStat]);
  const pValueErrorMessages = formContext.formState.errors?.information?.pValue?.selected
    ?.message as string;

  return (
    <Flex
      sx={(theme) => ({
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        rowGap: theme.spacing(3),
        columnGap: theme.spacing(6),
      })}
    >
      <FormRadioSelection
        name='information.calculated'
        label='ประเภทข้อสอบ'
        options={QUESTION_TYPE_OPTIONS}
        required
        row
        sx={{
          minWidth: 196,
          '.MuiFormGroup-root': {
            mt: (theme) => theme.spacing(1),
          },
        }}
      />
      <FormControl>
        <FormLabel required>ค่าความยาก (P)</FormLabel>
        <RadioGroup row sx={(theme) => ({ gap: theme.spacing(4), flexWrap: 'wrap' })}>
          <Flex
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <FormRadio
              name='information.pValue.selected'
              value='pInitial'
              label='P Initial'
              sx={{
                width: '100%',
                maxWidth: 96,
              }}
            />
            <PValueField
              placeholder='ระบุค่า P'
              controllerProps={{
                name: 'information.pValue.pInitial',
                control: formContext.control,
              }}
              sx={{
                maxWidth: 128,
                maxHeight: 44,
              }}
            />
          </Flex>
          <Flex
            sx={{
              flexDirection: 'column',
            }}
          >
            {mode === 'qcontent' ? (
              <Flex
                sx={{
                  alignItems: 'flex-start',
                  minWidth: '254px',
                }}
              >
                {status === QuestionStatus.Unpublished ? (
                  <FormRadio
                    name='information.pValue.selected'
                    value='pStat'
                    label='P Stat'
                    sx={{
                      width: '100%',
                      minWidth: 74,
                    }}
                  />
                ) : (
                  <Radio
                    name='information.pValue.selected'
                    value='pStat'
                    label='P Stat'
                    sx={{
                      width: '100%',
                      minWidth: 74,
                    }}
                    disabled
                  />
                )}
                <ReadonlyField
                  content={
                    <Flex
                      sx={{
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Text>{pStat}</Text>
                      <DifficultyText criteria={currentCriteria} />
                    </Flex>
                  }
                  sx={{
                    minWidth: '120px',
                  }}
                />
                <Flex
                  sx={() => ({
                    alignSelf: 'center',
                  })}
                >
                  <IconButton onClick={onClick}>
                    <BarChartIcon
                      sx={(theme) => ({
                        fontSize: '28px',
                        color: theme.palette.text.disabled,
                      })}
                    />
                  </IconButton>
                </Flex>
              </Flex>
            ) : null}
          </Flex>
        </RadioGroup>
        <FormHelperText error>{pValueErrorMessages}</FormHelperText>
      </FormControl>
      {mode === 'qcontent' ? (
        <FormNumberInput
          name='information.bIndex'
          label='ค่า B-Index'
          allowNegative
          placeholder='ระบุค่า B-index'
          maxDecimalPlaces={2}
          sx={{
            width: '100%',
            maxWidth: 128,
          }}
        />
      ) : null}
    </Flex>
  );
}
